import { loadDemoDb, saveDemoDb } from "./demoDb";
import { canHandleOsvita, handleOsvita } from "./modules/osvitaMock";

function nowIso() {
  return new Date().toISOString();
}

function toInt(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function nextId(items) {
  return (items || []).reduce((m, x) => Math.max(m, toInt(x?.id) || 0), 0) + 1;
}

function ok(data, status = 200) {
  return { data, status, statusText: "OK", headers: {} };
}

function notFound(message = "Not found") {
  return ok({ message }, 404);
}

function noMock(method, url) {
  return ok({ message: `DEMO: no mock for ${method} ${url}` }, 501);
}

function normalizeUrl(url) {
  // Accept "/api/x" or "api/x"
  return (url || "").replace(/^\//, "");
}

function safeJsonParse(maybeString) {
  if (typeof maybeString !== "string") return maybeString;
  try {
    return JSON.parse(maybeString);
  } catch (e) {
    return maybeString;
  }
}

export async function handleDemoRequest({ method, url, body }) {
  const m = String(method || "GET").toUpperCase();
  const u = normalizeUrl(url);
  const b = safeJsonParse(body) || {};

  // Module dispatch (modular approach)
  if (canHandleOsvita({ method: m, url: u })) {
    const res = handleOsvita({ method: m, url: u, body: b });
    if (res) return res;
  }

  const db = loadDemoDb();

  // -------- Auth (avoid touching real backend) --------
  if (m === "POST" && u === "api/auth/login") {
    // In demo mode we won't call this typically, but support it anyway.
    return ok({ id: "demo", name: "Demo User", role: "demo" });
  }
  if (m === "POST" && u === "api/auth/logout") {
    return ok({ ok: true });
  }
  if (m === "GET" && u === "api/auth/login") {
    return ok({ id: "demo", name: "Demo User", role: "demo" });
  }

  // -------- Public settings --------
  if (m === "GET" && u === "api/community-settings/public") {
    return ok(db.communitySettingsPublic || {});
  }
  if (m === "GET" && u === "api/ower-settings/public") {
    return ok(db.owerSettingsPublic || {});
  }

  // -------- SMS templates CRUD (visual-only) --------
  if (m === "GET" && u === "api/sms/templates") {
    return ok(db.smsTemplates || []);
  }
  if (m === "GET" && u.startsWith("api/sms/templates/")) {
    const id = toInt(u.split("/").pop());
    const item = (db.smsTemplates || []).find((x) => toInt(x.id) === id);
    return item ? ok(item) : notFound();
  }
  if (m === "POST" && u === "api/sms/templates") {
    const item = { id: nextId(db.smsTemplates), name: b.name || "Новий шаблон", text: b.text || "" };
    db.smsTemplates = [...(db.smsTemplates || []), item];
    saveDemoDb(db);
    return ok(item, 201);
  }
  if (m === "PUT" && u.startsWith("api/sms/templates/")) {
    const id = toInt(u.split("/").pop());
    const idx = (db.smsTemplates || []).findIndex((x) => toInt(x.id) === id);
    if (idx === -1) return notFound();
    const updated = { ...db.smsTemplates[idx], ...b, id: db.smsTemplates[idx].id };
    db.smsTemplates = db.smsTemplates.slice();
    db.smsTemplates[idx] = updated;
    saveDemoDb(db);
    return ok(updated);
  }
  if (m === "DELETE" && u.startsWith("api/sms/templates/")) {
    const id = toInt(u.split("/").pop());
    db.smsTemplates = (db.smsTemplates || []).filter((x) => toInt(x.id) !== id);
    saveDemoDb(db);
    return ok({ ok: true });
  }

  // -------- SMS preview/send/history (send is fake-success) --------
  if (m === "POST" && u === "api/sms/preview") {
    const templateId = toInt(b.templateId);
    const template = (db.smsTemplates || []).find((x) => toInt(x.id) === templateId);
    const text = b.text || template?.text || "Demo SMS preview...";
    return ok({ text });
  }
  if (m === "POST" && u === "api/sms/send") {
    // Visual-only: record in local history but no real sending
    const entry = {
      id: nextId(db.smsHistory),
      createdAt: nowIso(),
      phone: b.phone || "",
      text: b.text || "",
      templateId: b.templateId ?? null,
      debtor: b.debtor ?? null,
      status: "DEMO_SENT",
    };
    db.smsHistory = [...(db.smsHistory || []), entry];
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "POST" && u === "api/sms/send-batch") {
    // Visual-only
    saveDemoDb(db);
    return ok({ ok: true, demo: true });
  }
  if (m === "POST" && u === "api/sms/history") {
    return ok({ items: db.smsHistory || [], totalItems: (db.smsHistory || []).length });
  }
  if (m === "GET" && u.startsWith("api/sms/stats")) {
    return ok({ totalSent: (db.smsHistory || []).length, demo: true });
  }

  // -------- Debtors (used by fetch in DebtorList) --------
  if (m === "GET" && u === "api/debtor/with-phones") {
    return ok((db.debtors || []).map((d) => ({ id: d.id, name: d.name, phone: d.phone })));
  }
  if (m === "POST" && u === "api/debtor/list") {
    return ok({
      items: db.debtors || [],
      totalItems: (db.debtors || []).length,
      currentPage: 1,
      totalPages: 1,
    });
  }

  // -------- Kindergarten billing (used by fetch in KindergartenBilling) --------
  if (m === "GET" && u === "api/kindergarten/billing") {
    return ok(db.kindergartenBilling || { items: [], totalItems: 0, currentPage: 1, totalPages: 0 });
  }
  if (m === "POST" && u === "api/kindergarten/billing") {
    // Visual-only: allow UI to "save" filters/settings; echo back
    db.kindergartenBilling = { ...(db.kindergartenBilling || {}), ...(b || {}) };
    saveDemoDb(db);
    return ok(db.kindergartenBilling);
  }
  if (m === "POST" && u === "api/kindergarten/billing/parse-pdf") {
    // Destructive / heavy action -> fake-success
    return ok({ ok: true, demo: true, message: "DEMO: parse-pdf is disabled" });
  }

  // -------- Default --------
  return noMock(m, u);
}

